// src/cockpit4/workspaces/BossViewWorkspace.tsx
// ============================================
// 老板视图（决策中枢）— P0② 差距补齐（李开复「老板 AI」对标）
//
// 定位：给老板"上帝视角"—— 承诺账本 / 风险卡片 / 证据链 / 会议闭环
// 对比李开复老板 AI 六大能力：承诺账本(承诺兑现) / 执行驾驶舱(风险卡片)
// / 证据链(结论可溯源) / 会议领航员(会议闭环) 的第一步落地。
//
// 数据全部落 Obsidian Vault（铁律：唯一真相源，禁止 mock/硬编码假数据），
// 统一走 /vault-api read/write/list，相对 vaultRoot 路径。
//   - 承诺账本：10_Operations（运营层）/Commitments/承诺账本.md
//   - 风险卡片：10_Operations（运营层）/Risks/风险卡片.md
//   - 会议纪要：10_Operations（运营层）/Meetings/会议-{date}.md
//   - 决策留痕：10_Operations（运营层）/Decisions/决策-{ts}.md
// ============================================

import { useEffect, useMemo, useState, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
// Phase 3 P0-3：页面顶部业务说明 + 跨模块跳转
import WorkspaceBizBar from "../components/WorkspaceBizBar";
import {
  Crown, NotepadText, AlertTriangle, Link2, CalendarPlus, CheckCircle2,
  XCircle, Plus, RefreshCw, Loader2, FileText, ShieldAlert, BookCheck,
  ArrowRight, Clock, UserRoundCheck, ListChecks, Expand, Gavel, TrendingUp,
  Eye, Users,
} from "lucide-react";
import { BossSummary, DebateRoom, FunnelPanel, BossQA, TalentReview } from "../components/BossExtras";

// ============================================
// 常量：Vault 相对路径
// ============================================
const BASE = "10_Operations（运营层）";
const P_COMMIT = `${BASE}/Commitments/承诺账本.md`;
const P_RISK = `${BASE}/Risks/风险卡片.md`;
const P_MEETING = `${BASE}/Meetings`;
const P_DECISION = `${BASE}/Decisions`;

// ============================================
// Vault 读写封装
// ============================================
async function readMd(relPath: string): Promise<string> {
  try {
    const r = await fetch(`/vault-api/read?path=${encodeURIComponent(relPath)}`);
    if (!r.ok) return "";
    const j = await r.json();
    return j?.success && typeof j.content === "string" ? j.content : "";
  } catch {
    return "";
  }
}

async function writeMd(relPath: string, content: string): Promise<boolean> {
  try {
    const r = await fetch(`/vault-api/write?path=${encodeURIComponent(relPath)}`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ content }),
    });
    const j = await r.json();
    return !!j?.success;
  } catch {
    return false;
  }
}

async function listDir(relPath: string): Promise<string[]> {
  try {
    const r = await fetch(`/vault-api/list?dir=${encodeURIComponent(relPath)}&ext=.md`);
    const j = await r.json();
    const files: any[] = j?.files || [];
    return files.map((f: any) => String(f.name || "")).filter(Boolean);
  } catch {
    return [];
  }
}

// ============================================
// 类型
// ============================================
interface Commitment {
  id: string;
  person: string;      // 承诺人
  content: string;     // 承诺内容
  due: string;         // 截止日期 YYYY-MM-DD
  accept: string;      // 验收标准
  status: "open" | "done" | "risk"; // 承诺/兑现/逾期风险
  source?: string;     // 来源（会议/计划/人工），用于证据链
  updated: string;
}

function parseCommitments(md: string): Commitment[] {
  const out: Commitment[] = [];
  const lines = md.split("\n");
  for (const ln of lines) {
    // 行格式：| **person** | content | due | accept | status | source |
    const trimmed = ln.trim();
    if (!trimmed.startsWith("|")) continue;
    if (trimmed.includes("---")) continue; // 表头分隔行
    const cells = trimmed.split("|").map((s) => s.trim());
    const person = cells[1]?.replace(/\*\*/g, "").trim();
    const content = cells[2]?.trim();
    const due = cells[3]?.trim();
    const accept = cells[4]?.trim();
    const status = cells[5]?.trim();
    if (!person || !content) continue;
    out.push({
      id: `C${Math.abs([...person, content, due].join("").split("").reduce((a, c) => a + c.charCodeAt(0), 0))}`,
      person,
      content,
      due: due || "",
      accept: accept || "完成交付",
      status: status === "done" ? "done" : status === "risk" ? "risk" : "open",
      source: cells[6]?.trim() || "人工",
      updated: new Date().toISOString().slice(0, 10),
    });
  }
  return out;
}

function commitmentsToMd(list: Commitment[]): string {
  const head =
    "---\ntype: commitment_ledger\nupdated: " +
    new Date().toISOString().slice(0, 10) +
    "\n---\n\n# 承诺账本\n\n> 李开复「老板 AI · 承诺账本」映射：老板会忘记，老板 AI 不会忘记。\n> 对账：周复盘自动比对 截止日期 vs 验收标准 判定 open/done/risk。\n\n| 承诺人 | 承诺内容 | 截止 | 验收标准 | 状态 | 来源 |\n| --- | --- | --- | --- | --- | --- |\n";
  const body = list
    .map(
      (c) =>
        `| **${c.person}** | ${c.content} | ${c.due} | ${c.accept} | ${c.status} | ${c.source || "人工"} |`
    )
    .join("\n");
  return head + body + "\n";
}

interface RiskCard {
  id: string;
  title: string;
  level: "high" | "medium" | "low";
  detail: string;
  status: "open" | "closed";
  evidence: string;  // 证据链：来源文件/记录
  createdAt: string;
}

function parseRisks(md: string): RiskCard[] {
  const out: RiskCard[] = [];
  const lines = md.split("\n");
  for (const ln of lines) {
    const trimmed = ln.trim();
    if (!trimmed.startsWith("|")) continue;
    if (trimmed.includes("---")) continue;
    const cells = trimmed.split("|").map((s) => s.trim());
    const status = cells[1]?.trim();
    const level = cells[2]?.trim();
    const title = cells[3]?.trim();
    if (!title) continue;
    out.push({
      id: `R${Math.abs([...title, level].join("").split("").reduce((a, c) => a + c.charCodeAt(0), 0))}`,
      status: status === "closed" ? "closed" : "open",
      level: level === "high" || level === "low" ? level : "medium",
      title,
      detail: cells[4]?.trim() || "",
      evidence: cells[5]?.trim() || "",
      createdAt: new Date().toISOString().slice(0, 10),
    });
  }
  return out;
}

function risksToMd(list: RiskCard[]): string {
  const head =
    "---\ntype: risk_cards\nupdated: " +
    new Date().toISOString().slice(0, 10) +
    "\n---\n\n# 风险卡片\n\n> 执行驾驶舱 · 风险直达 CEO：断更/承诺逾期/回款异常/信源掉线 自动生成卡片，每条可沿证据链追溯。\n\n| 状态 | 级别 | 风险 | 详情 | 证据链 |\n| --- | --- | --- | --- | --- |\n";
  const body = list
    .map(
      (r) =>
        `| ${r.status} | ${r.level} | ${r.title} | ${r.detail} | ${r.evidence} |`
    )
    .join("\n");
  return head + body + "\n";
}

// ============================================
// 顶部 Tab 配置
// ============================================
const TABS = [
  { id: "summary", label: "老板摘要", icon: TrendingUp, color: "#F59E0B" },
  { id: "qa", label: "老板问答", icon: Eye, color: "#38BDF8" },
  { id: "commit", label: "承诺账本", icon: BookCheck, color: "#38BDF8" },
  { id: "talent", label: "人才盘点", icon: Users, color: "#22D3EE" },
  { id: "risk", label: "风险卡片", icon: ShieldAlert, color: "#FB7185" },
  { id: "evidence", label: "证据链", icon: Link2, color: "#34D399" },
  { id: "meeting", label: "会议闭环", icon: CalendarPlus, color: "#A78BFA" },
  { id: "debate", label: "辩论室", icon: Gavel, color: "#8B5CF6" },
  { id: "funnel", label: "获客漏斗", icon: TrendingUp, color: "#22D3EE" },
];

export default function BossViewWorkspace() {
  const [tab, setTab] = useState("commit");
  const [commitments, setCommitments] = useState<Commitment[]>([]);
  const [risks, setRisks] = useState<RiskCard[]>([]);
  const [decisions, setDecisions] = useState<string[]>([]);
  const [loadErr, setLoadErr] = useState("");
  const [busy, setBusy] = useState(false);

  const loadAll = useCallback(async () => {
    setBusy(true);
    setLoadErr("");
    try {
      const [cm, rk, dv] = await Promise.all([
        readMd(P_COMMIT),
        readMd(P_RISK),
        listDir(P_DECISION),
      ]);
      setCommitments(parseCommitments(cm));
      setRisks(parseRisks(rk));
      setDecisions(dv);
    } catch (e: any) {
      setLoadErr(e?.message || "加载失败");
    } finally {
      setBusy(false);
    }
  }, []);

  useEffect(() => {
    void loadAll();
  }, [loadAll]);

  // 顶部横幅：未关闭风险速览（执行驾驶舱雏形）
  const openRisks = useMemo(() => risks.filter((r) => r.status === "open"), [risks]);
  const riskBanner = openRisks.slice(0, 3);

  return (
    <div className="h-full flex flex-col overflow-hidden">
      {/* 顶部标题条 */}
      <div className="flex items-center gap-2 px-4 pt-3 pb-2">
        <Crown size={15} className="text-amber-400" />
        <span className="text-[13px] font-semibold text-white tracking-tight">老板视图</span>
        <span className="text-[9px] font-mono text-gray-600 uppercase tracking-wider">Decision Hub · 对标李开复「老板 AI」</span>
        <div className="flex-1" />
        <button
          onClick={() => void loadAll()}
          className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-md border border-white/[0.08] bg-white/[0.02] text-[10px] font-mono text-gray-400 hover:text-white transition-colors"
        >
          <RefreshCw size={11} className={busy ? "animate-spin" : ""} /> 刷新真实数据
        </button>
      </div>

      {/* Phase3 P0-3 业务说明 + 跨模块跳转 */}
      <div className="px-4 pb-2">
        <WorkspaceBizBar
          desc="老板视图：追踪承诺、风险、关键决策，查看未闭环业务风险"
          accent="#F59E0B"
          jumps={[
            { label: "查看经营诊断", to: "workflow" },
            { label: "查看营收客户", to: "ceo" },
          ]}
        />
      </div>

      {/* 风险速览横幅 */}
      {riskBanner.length > 0 && (
        <div className="mx-4 mb-2 flex items-center gap-2 rounded-md border border-rose-500/30 bg-rose-500/[0.06] px-3 py-2">
          <AlertTriangle size={12} className="text-rose-400 flex-shrink-0" />
          <span className="text-[10px] font-mono text-rose-300">{openRisks.length} 条未关闭风险</span>
          <div className="flex gap-1.5 overflow-hidden">
            {riskBanner.map((r) => (
              <span key={r.id + r.title} className="text-[9px] font-mono px-1.5 py-0.5 rounded bg-rose-500/20 text-rose-200 text-ellipsis whitespace-nowrap max-w-[160px] truncate">
                {r.title}
              </span>
            ))}
          </div>
        </div>
      )}
      {loadErr && (
        <div className="mx-4 mb-2 text-[10px] font-mono text-amber-400">加载提示：{loadErr}</div>
      )}

      {/* Tab 栏 */}
      <div className="flex items-center gap-1 px-4 pb-2 flex-wrap">
        {TABS.map((t) => {
          const I = t.icon;
          const active = tab === t.id;
          return (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-[10px] font-mono uppercase tracking-wider transition-all ${
                active ? "bg-white/[0.06] text-white border border-white/[0.12]" : "text-gray-500 hover:bg-white/[0.03] border border-transparent"
              }`}
            >
              <I size={11} style={{ color: active ? t.color : undefined }} />
              {t.label}
            </button>
          );
        })}
      </div>

      {/* 内容区 */}
      <div className="flex-1 overflow-y-auto px-4 pb-4">
        {tab === "summary" && <BossSummary />}
        {tab === "qa" && <BossQA />}
        {tab === "commit" && (
          <CommitmentLedger list={commitments} setList={setCommitments} />
        )}
        {tab === "talent" && <TalentReview commitments={commitments} risks={risks} />}
        {tab === "risk" && <RiskCardList list={risks} setList={setRisks} commitments={commitments} loadAll={loadAll} />}
        {tab === "evidence" && <EvidenceChain decisions={decisions} commitments={commitments} risks={risks} />}
        {tab === "meeting" && <MeetingClose loadAll={loadAll} />}
        {tab === "debate" && <DebateRoom />}
        {tab === "funnel" && <FunnelPanel />}
      </div>
    </div>
  );
}

// ============================================
// 承诺账本
// ============================================
function CommitmentLedger({
  list,
  setList,
}: {
  list: Commitment[];
  setList: (l: Commitment[]) => void;
}) {
  const [person, setPerson] = useState("");
  const [content, setContent] = useState("");
  const [due, setDue] = useState("");
  const [accept, setAccept] = useState("");
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState("");

  const today = new Date().toISOString().slice(0, 10);

  const save = async () => {
    if (!person.trim() || !content.trim() || !due) {
      setToast("请填写 承诺人 / 承诺内容 / 截止日期");
      return;
    }
    setSaving(true);
    const next: Commitment = {
      id: `C${Date.now()}`,
      person: person.trim(),
      content: content.trim(),
      due,
      accept: accept.trim() || "完成交付",
      status: "open",
      source: "人工",
      updated: today,
    };
    const merged = [...list, next];
    const ok = await writeMd(P_COMMIT, commitmentsToMd(merged));
    if (ok) {
      setList(merged);
      setToast("已写入承诺账本");
      setPerson(""); setContent(""); setDue(""); setAccept("");
    } else {
      setToast("写盘失败");
    }
    setSaving(false);
    setTimeout(() => setToast(""), 2200);
  };

  // 逾期自动标 risk（对账）
  const reconciled = useMemo(
    () =>
      list.map((c) => {
        if (c.status !== "open") return c;
        if (c.due && c.due < today) return { ...c, status: "risk" as const };
        return c;
      }),
    [list, today]
  );

  const done = reconciled.filter((c) => c.status === "done").length;
  const risk = reconciled.filter((c) => c.status === "risk").length;

  return (
    <div className="space-y-3">
      {/* 录入卡片 */}
      <div className="rounded-xl border border-white/[0.06] bg-white/[0.02] p-3 space-y-2">
        <div className="flex items-center gap-2 text-[10px] font-mono text-cyan-300 uppercase tracking-wider">
          <NotepadText size={11} /> 录入新承诺
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2">
          <input
            value={person} onChange={(e) => setPerson(e.target.value)} placeholder="承诺人"
            className="px-2 py-1.5 rounded-md border border-white/[0.08] bg-black/20 text-[11px] text-white outline-none focus:border-cyan-500/40"
          />
          <input
            value={content} onChange={(e) => setContent(e.target.value)} placeholder="承诺内容"
            className="px-2 py-1.5 rounded-md border border-white/[0.08] bg-black/20 text-[11px] text-white outline-none focus:border-cyan-500/40"
          />
          <input
            type="date" value={due} onChange={(e) => setDue(e.target.value)}
            className="px-2 py-1.5 rounded-md border border-white/[0.08] bg-black/20 text-[11px] text-gray-300 outline-none focus:border-cyan-500/40"
          />
          <input
            value={accept} onChange={(e) => setAccept(e.target.value)} placeholder="验收标准（可选）"
            className="px-2 py-1.5 rounded-md border border-white/[0.08] bg-black/20 text-[11px] text-white outline-none focus:border-cyan-500/40"
          />
        </div>
        <button
          onClick={() => void save()}
          disabled={saving}
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-cyan-500/20 text-cyan-200 text-[11px] font-mono hover:bg-cyan-500/30 transition-colors disabled:opacity-50"
        >
          {saving ? <Loader2 size={11} className="animate-spin" /> : <Plus size={11} />}
          写入承诺账本
        </button>
        {toast && <div className="text-[10px] font-mono text-emerald-400">{toast}</div>}
      </div>

      {/* 对账概览 */}
      <div className="flex gap-2">
        {[
          { l: "未完成", v: reconciled.filter((c) => c.status === "open").length, c: "#38BDF8" },
          { l: "已兑现", v: done, c: "#34D399" },
          { l: "逾期风险", v: risk, c: "#FB7185" },
        ].map((s) => (
          <div key={s.l} className="flex-1 rounded-lg border border-white/[0.06] bg-white/[0.02] px-3 py-2">
            <div className="text-[9px] font-mono text-gray-500 uppercase tracking-wider">{s.l}</div>
            <div className="text-xl font-bold text-white" style={{ color: s.c }}>{s.v}</div>
          </div>
        ))}
      </div>

      {/* 对账明细 */}
      <div className="rounded-xl border border-white/[0.06] bg-white/[0.02] overflow-hidden">
        <div className="flex items-center gap-2 px-3 py-2 border-b border-white/[0.06]">
          <ListChecks size={11} className="text-cyan-400" />
          <span className="text-[10px] font-mono text-gray-300 uppercase tracking-wider">对账明细（逾期自动标风险）</span>
        </div>
        {reconciled.length === 0 && (
          <div className="px-3 py-8 text-center text-[11px] font-mono text-gray-600">暂无承诺数据 — 请在录入卡片添加第一条真实承诺</div>
        )}
        {reconciled.length > 0 && (
          <table className="w-full text-left text-[11px]">
            <thead>
              <tr className="text-[9px] font-mono text-gray-500 uppercase tracking-wider border-b border-white/[0.04]">
                <th className="px-3 py-2">承诺人</th><th className="px-3 py-2">承诺内容</th>
                <th className="px-3 py-2">截止</th><th className="px-3 py-2">验收标准</th>
                <th className="px-3 py-2">状态</th>
              </tr>
            </thead>
            <tbody>
              {reconciled.map((c) => (
                <tr key={c.id} className="border-b border-white/[0.03] hover:bg-white/[0.02]">
                  <td className="px-3 py-2 text-cyan-200">{c.person}</td>
                  <td className="px-3 py-2 text-gray-200">{c.content}</td>
                  <td className="px-3 py-2 font-mono text-gray-400">{c.due}</td>
                  <td className="px-3 py-2 text-gray-400">{c.accept}</td>
                  <td className="px-3 py-2">
                    <ToggleStatus c={c} list={reconciled} setList={setList} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

function ToggleStatus({ c, list, setList }: { c: Commitment; list: Commitment[]; setList: (l: Commitment[]) => void }) {
  const next = c.status === "done" ? "open" : "done";
  return (
    <button
      onClick={() => {
        const updated = list.map((x) => (x.id === c.id ? { ...x, status: next as Commitment["status"], updated: new Date().toISOString().slice(0, 10) } : x));
        void writeMd(P_COMMIT, commitmentsToMd(updated)).then(() => setList(updated));
      }}
      className={`flex items-center gap-1 px-2 py-0.5 rounded-full text-[9px] font-mono ${
        c.status === "done"
          ? "bg-emerald-500/20 text-emerald-300"
          : c.status === "risk"
          ? "bg-rose-500/20 text-rose-300"
          : "bg-sky-500/20 text-sky-300"
      }`}
    >
      {c.status === "done" ? <CheckCircle2 size={9} /> : c.status === "risk" ? <AlertTriangle size={9} /> : <Clock size={9} />}
      {c.status === "done" ? "已兑现" : c.status === "risk" ? "逾期风险" : "未完成"}
    </button>
  );
}

// ============================================
// 风险卡片
// ============================================
function RiskCardList({
  list, setList, commitments, loadAll,
}: {
  list: RiskCard[];
  setList: (l: RiskCard[]) => void;
  commitments: Commitment[];
  loadAll: () => Promise<void>;
}) {
  const [scanning, setScanning] = useState(false);
  const today = new Date().toISOString().slice(0, 10);

  // 自动扫描风险（真实数据：基于承诺账本逾期 + 已有卡片状态）
  const scan = async () => {
    setScanning(true);
    const found: RiskCard[] = [...list];
    const overdue = commitments.filter((c) => c.status === "open" && c.due < today);
    for (const c of overdue) {
      if (!found.some((r) => r.title.includes(c.content.slice(0, 12)))) {
        found.push({
          id: `R${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
          title: `承诺逾期：${c.person} · ${c.content.slice(0, 20)}`,
          level: "high",
          detail: `承诺人 ${c.person}，截止 ${c.due} 仍未兑现（来源承诺账本）`,
          status: "open",
          evidence: `承诺账本 → ${c.id}`,
          createdAt: today,
        });
      }
    }
    const ok = await writeMd(P_RISK, risksToMd(found));
    if (ok) setList(found);
    setScanning(false);
    void loadAll();
  };

  const toggle = (id: string) => {
    const updated = list.map((r) => (r.id === id ? { ...r, status: r.status === "open" ? "closed" as const : "open" as const } : r));
    void writeMd(P_RISK, risksToMd(updated)).then(() => setList(updated));
  };

  const open = list.filter((r) => r.status === "open");
  const closed = list.filter((r) => r.status === "closed");

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-3 rounded-xl border border-white/[0.06] bg-white/[0.02] p-3">
        <div className="flex-1">
          <div className="text-[10px] font-mono text-rose-300 uppercase tracking-wider flex items-center gap-1.5">
            <ShieldAlert size={11} /> 执行驾驶舱 · 风险直达 CEO
          </div>
          <div className="mt-1 text-[11px] text-gray-400">
            自动扫描承诺账本：逾期未兑现承诺 → 生成高风险卡片，可沿证据链追溯。人工扫码后点击"确认/关闭"。
          </div>
        </div>
        <button
          onClick={() => void scan()}
          disabled={scanning}
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-rose-500/15 text-rose-200 text-[11px] font-mono hover:bg-rose-500/25 transition-colors disabled:opacity-50"
        >
          {scanning ? <Loader2 size={11} className="animate-spin" /> : <RefreshCw size={11} />}
          {open.length} 开放 · 扫描新风险
        </button>
      </div>

      {list.length === 0 && (
        <div className="px-3 py-10 text-center text-[11px] font-mono text-gray-600">
          暂无风险卡片 — 点击"扫描新风险"基于承诺账本逾期自动生成；或是否有真实异常需要记录？
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
        {[...open, ...closed].map((r) => (
          <div key={r.id} className={`rounded-xl border p-3 ${r.status === "open" ? "border-rose-500/30 bg-rose-500/[0.05]" : "border-white/[0.06] bg-white/[0.02] opacity-60"}`}>
            <div className="flex items-start justify-between gap-2">
              <div className="flex items-center gap-1.5 min-w-0">
                <AlertTriangle size={12} className={r.level === "high" ? "text-rose-400 flex-shrink-0" : "text-amber-400 flex-shrink-0"} />
                <span className={`text-[11px] font-medium truncate ${r.status === "open" ? "text-rose-200" : "text-gray-400"}`}>{r.title}</span>
              </div>
              <button onClick={() => toggle(r.id)} className="flex-shrink-0 px-2 py-0.5 rounded-full text-[9px] font-mono bg-white/[0.06] text-gray-300 hover:bg-white/[0.12]">
                {r.status === "open" ? "确认关闭" : "重开"}
              </button>
            </div>
            {r.detail && <p className="mt-1.5 text-[10px] text-gray-400 line-clamp-2">{r.detail}</p>}
            {r.evidence && (
              <div className="mt-2 flex items-center gap-1.5 text-[9px] font-mono text-emerald-400">
                <Link2 size={9} /> 证据链：{r.evidence}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

// ============================================
// 证据链
// ============================================
function EvidenceChain({
  decisions, commitments, risks,
}: {
  decisions: string[];
  commitments: Commitment[];
  risks: RiskCard[];
}) {
  const items = useMemo(() => {
    const out: { type: string; title: string; target: string; color: string }[] = [];
    for (const d of decisions) out.push({ type: "决策", title: d.replace(/\.md$/i, ""), target: `${P_DECISION}/${d}`, color: "#A78BFA" });
    for (const r of risks.filter((x) => x.evidence)) out.push({ type: "风险", title: r.title, target: r.evidence, color: "#FB7185" });
    for (const c of commitments) out.push({ type: "承诺", title: `${c.person} · ${c.content.slice(0, 24)}`, target: P_COMMIT, color: "#38BDF8" });
    return out;
  }, [decisions, commitments, risks]);

  return (
    <div className="space-y-3">
      <div className="rounded-xl border border-white/[0.06] bg-white/[0.02] p-3">
        <div className="text-[10px] font-mono text-emerald-300 uppercase tracking-wider flex items-center gap-1.5">
          <Link2 size={11} /> 证据链浏览器（Rule 04：结论可追溯）
        </div>
        <p className="mt-1 text-[11px] text-gray-400">
          每条经营结论/风险/承诺都可沿证据链追溯到源文件。点击可展开原始记录（决策/承诺/风险各自落盘在对应 Vault 目录）。
        </p>
      </div>
      {items.length === 0 && (
        <div className="px-3 py-10 text-center text-[11px] font-mono text-gray-600">暂无证据链条目 — 产生承诺/风险/决策后自动聚合</div>
      )}
      <div className="space-y-1.5">
        {items.map((it, i) => (
          <div key={i} className="flex items-center gap-2 rounded-lg border border-white/[0.05] bg-white/[0.015] px-3 py-2">
            <span className="px-1.5 py-0.5 rounded text-[9px] font-mono" style={{ background: `${it.color}22`, color: it.color }}>{it.type}</span>
            <span className="flex-1 truncate text-[11px] text-gray-300">{it.title}</span>
            <span className="text-[9px] font-mono text-gray-600 truncate max-w-[220px]">{it.target}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ============================================
// 会议闭环（手工录入起步）
// ============================================
function MeetingClose({ loadAll }: { loadAll: () => Promise<void> }) {
  const [topic, setTopic] = useState("");
  const [conclusion, setConclusion] = useState("");
  const [division, setDivision] = useState("");
  const [items, setItems] = useState<{ person: string; action: string; due: string }[]>([{ person: "", action: "", due: "" }]);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState("");
  const today = new Date().toISOString().slice(0, 10);

  const setItem = (i: number, k: keyof (typeof items)[number], v: string) => {
    setItems((prev) => prev.map((it, idx) => (idx === i ? { ...it, [k]: v } : it)));
  };

  const save = async () => {
    if (!topic.trim()) { setToast("请填写会议主题"); return; }
    setSaving(true);
    const ts = new Date().toISOString().slice(0, 16).replace("T", " ");
    const md =
      `---\ntype: meeting_minutes\ndate: ${today}\n---\n\n# 会议纪要：${topic.trim()}\n\n> 李开复「会议领航员」闭环：结论 / 行动项(责任人+期限) / 分歧点 → 行动项自动落入承诺账本。\n\n## 结论\n\n${conclusion.trim() || "（待补充）"}\n\n## 行动项\n\n| 责任人 | 行动 | 截止 |\n| --- | --- | --- |\n` +
      items.filter((i) => i.person.trim() && i.action.trim()).map((i) => `| ${i.person.trim()} | ${i.action.trim()} | ${i.due || today} |`).join("\n") +
      `\n\n## 分歧点\n\n${division.trim() || "（无）"}\n\n---\n记录时间：${ts}\n`;
    const ok = await writeMd(`${P_MEETING}/会议-${today}.md`, md);
    if (ok) {
      // 联动：行动项写入承诺账本
      const cm = parseCommitments(await readMd(P_COMMIT));
      const actions = items.filter((i) => i.person.trim() && i.action.trim());
      if (actions.length > 0) {
        const merged = [
          ...cm,
          ...actions.map((a) => ({
            id: `C${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
            person: a.person.trim(),
            content: a.action.trim(),
            due: a.due || today,
            accept: "按会议纪要验收",
            status: "open" as const,
            source: `会议 ${topic.trim()}`,
            updated: today,
          })),
        ];
        await writeMd(P_COMMIT, commitmentsToMd(merged));
      }
      setToast("会议纪要已落盘，行动项已写入承诺账本");
      loadAll();
      setTopic(""); setConclusion(""); setDivision(""); setItems([{ person: "", action: "", due: "" }]);
    } else {
      setToast("落盘失败");
    }
    setSaving(false);
    setTimeout(() => setToast(""), 2600);
  };

  return (
    <div className="space-y-3">
      <div className="rounded-xl border border-white/[0.06] bg-white/[0.02] p-3 space-y-2">
        <div className="text-[10px] font-mono text-purple-300 uppercase tracking-wider flex items-center gap-1.5">
          <CalendarPlus size={11} /> 会议闭环（手工录入）
        </div>
        <div className="text-[10px] text-gray-500">会后录入：结论 / 行动项(责任人+期限) / 分歧点。行动项自动联动写入承诺账本。</div>
        <input value={topic} onChange={(e) => setTopic(e.target.value)} placeholder="会议主题" className="w-full px-2 py-1.5 rounded-md border border-white/[0.08] bg-black/20 text-[11px] text-white outline-none focus:border-purple-500/40" />
        <textarea value={conclusion} onChange={(e) => setConclusion(e.target.value)} placeholder="会议结论（换行分隔要点）" rows={2} className="w-full px-2 py-1.5 rounded-md border border-white/[0.08] bg-black/20 text-[11px] text-white outline-none focus:border-purple-500/40 resize-none" />
        {items.map((it, i) => (
          <div key={i} className="grid grid-cols-1 sm:grid-cols-3 gap-2">
            <input value={it.person} onChange={(e) => setItem(i, "person", e.target.value)} placeholder="责任人" className="px-2 py-1.5 rounded-md border border-white/[0.08] bg-black/20 text-[11px] text-white outline-none focus:border-purple-500/40" />
            <input value={it.action} onChange={(e) => setItem(i, "action", e.target.value)} placeholder="行动项" className="px-2 py-1.5 rounded-md border border-white/[0.08] bg-black/20 text-[11px] text-white outline-none focus:border-purple-500/40" />
            <div className="flex gap-1.5">
              <input type="date" value={it.due} onChange={(e) => setItem(i, "due", e.target.value)} className="flex-1 px-2 py-1.5 rounded-md border border-white/[0.08] bg-black/20 text-[11px] text-gray-300 outline-none focus:border-purple-500/40" />
              <button onClick={() => setItems((p) => p.filter((_, x) => x !== i))} className="px-2 text-gray-500 hover:text-rose-400 text-[12px]">×</button>
            </div>
          </div>
        ))}
        <div className="flex items-center gap-2">
          <button onClick={() => setItems((p) => [...p, { person: "", action: "", due: "" }])} className="px-3 py-1.5 rounded-md border border-white/[0.08] text-gray-400 text-[11px] font-mono hover:text-white">+ 行动项</button>
          <div className="flex-1" />
          <button onClick={() => void save()} disabled={saving} className="flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-purple-500/20 text-purple-200 text-[11px] font-mono hover:bg-purple-500/30 transition-colors disabled:opacity-50">
            {saving ? <Loader2 size={11} className="animate-spin" /> : <Expand size={11} />} 落盘会议纪要 + 联动承诺账本
          </button>
        </div>
        {toast && <div className="text-[10px] font-mono text-emerald-400">{toast}</div>}
      </div>
    </div>
  );
}